/** @format */

import { memo } from 'react'

import styles from 'dir/styles/courses.module.css'

export const _4 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>4 - Обзор основных тегов</h2>
    <p className={styles.description}>
      HTML-документ должен иметь смысл, как страница из газеты, на ней есть секции, заголовки, списки, изображения,
      абзацы текст и т.д. Для всего этого богатства типов контента есть соответствующие теги, которые несут в себе
      смысл, тем самым описывая контент. Давайте разберем основные из них.
    </p>
    <p className={styles.notificationWarn}>
      В конце секции будет один большой пример включающий в себя рассмотренные теги.
    </p>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.1 - Большие смысловые разделы</h3>
      <p className={styles.description}>
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.w3.org/TR/html53/sections.html#the-article-element"
        >
          article
        </a>{' '}
        — независимая, отделяемая, и имеющая значение вне, часть документа, например твит, статья, виджет с рекламой.
        Желателен заголовок.
      </p>
      <p className={styles.description}>
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.w3.org/TR/html53/sections.html#the-section-element"
        >
          section
        </a>{' '}
        — смысловой раздел документа. Неотделяемый от основного документа, в отличие от{' '}
        <code className={styles.code}>article</code>. Желателен заголовок.
      </p>
      <p className={styles.description}>
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.w3.org/TR/html53/sections.html#the-nav-element"
        >
          nav
        </a>{' '}
        — навигационный раздел со ссылками на другие страницы или другие части страниц. Используйте для основной
        навигации, а не для всех групп ссылок в документе.
      </p>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.2 - Заголовки {`<h1>...<h6>`}</h3>
      <p className={styles.description}>
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.w3.org/TR/html53/sections.html#the-h1-h2-h3-h4-h5-and-h6-elements"
        >
          Эта группа тегов
        </a>{' '}
        определяет текстовые заголовки смысловых разделов разного уровня, которые показывают относительную важность
        секции, расположенной после заголовка.
      </p>
      <p className={styles.description}>
        Должны использоваться только для выделения заголовков нового раздела или подраздела. При использовании
        заголовков необходимо учитывать их иерархию, т.е. за <code className={styles.code}>h1</code> должен следовать{' '}
        <code className={styles.code}>h2</code> и т.д.
      </p>
      <p className={styles.description}>
        Обратите внимание, что тег <code className={styles.code}>h1</code> используется только один раз, как основной
        заголовок страницы. На главных страницах не всегда рисуют заголовок первого уровня.{' '}
        <code className={styles.code}>h2</code> до <code className={styles.code}>h6</code>, однако, могут использоваться
        сколько угодно, но их всегда следует использовать по порядку, указывая важность и иерархию заголовоков контента.
        Например, <code className={styles.code}>h4</code> должен быть подзаголовком{' '}
        <code className={styles.code}>h3</code>, который должен быть подзаголовком{' '}
        <code className={styles.code}>h2</code>.
      </p>
      <iframe
        height="300"
        scrolling="no"
        title="rotman_html_4_2"
        src="https://codepen.io/rotmanyan/embed/KKXKKvP?default-tab=html%2Cresult&editable=true&theme-id=dark"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />

      <p className={styles.notificationWarn}>
        Не определяйте уровень заголовка по размеру текста на макете. Не весь большой текст – заголовки. Заголовок -
        оглавляет секцию контента.
      </p>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.3 - Потоковое содержимое</h3>
      <p className={styles.description}>
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.w3.org/TR/html53/sections.html#the-header-element"
        >
          header
        </a>{' '}
        — вводная часть смыслового раздела или всего сайта, обычно содержит подсказки и навигацию. Часто используется
        как шапка всей страницы или сложной секции. Может быть несколько на странице.
      </p>
      <p className={styles.description}>
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.w3.org/TR/html53/grouping-content.html#the-main-element"
        >
          main
        </a>{' '}
        — основное, не повторяющееся на других страницах, содержание. Может быть только один на странице.
      </p>
      <p className={styles.description}>
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.w3.org/TR/html53/sections.html#the-footer-element"
        >
          footer
        </a>{' '}
        — финальная часть раздела или всего сайта, часто содержит копирайт, список ссылок на социальные сети, контактную
        информацию и так далее. Может быть несколько на странице.
      </p>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.4 - Списки</h3>
      <p className={styles.description}>
        Списки предоставляют возможность упорядочить и систематизировать коллекции и представить их в наглядном и
        удобном для пользователя виде. Каждый список представляет собой контейнер, внутри которого располагаются
        элементы списка.
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>ol</code> — создает нумерованный список, т.е. каждый элемент списка начинается с
          числа. Браузер нумерует элементы по порядку автоматически и если удалить один или несколько элементов такого
          списка, то остальные номера будут автоматически пересчитаны.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>ul</code> — создает маркированный список, каждый элемент которого начинается с
          небольшого символа — маркера.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>li</code> — определяет отдельный элемент списка.
        </li>
      </ul>
      <iframe
        height="300"
        scrolling="no"
        title="rotman_html_4_4"
        src="https://codepen.io/rotmanyan/embed/dyVyyzJ?default-tab=html%2Cresult&editable=true&theme-id=dark"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.5 - Параграф</h3>
      <p className={styles.description}>
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.w3.org/TR/html53/grouping-content.html#the-p-element"
        >
          Параграф
        </a>{' '}
        — универсальный контейнер для группировки мелких фразовых элементов, отделения их друг от друга, и дальнейшей
        стилизации. Используется не только для указания параграфов, это скорее структурный тег нежели смысловой.
      </p>
      <iframe
        height="300"
        scrolling="no"
        title="rotman_html_4_5"
        src="https://codepen.io/rotmanyan/embed/dyVyyVJ?default-tab=html%2Cresult&editable=true&theme-id=dark"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
      <p className={styles.description}>
        Часто необходимо заполнить тег текстом, когда самого текста, который будет размещаться на сайте ещё нет. Для
        того, чтобы текст для заполнения был похожим на реальный используют специальный заполнитель. Это набор слов,
        пришедший из типографии (текст-рыба).
      </p>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.6 - Универсальный блочный контейнер</h3>
      <p className={styles.description}>
        В то время как большинство HTML-тегов определяют значение ( <code className={styles.code}>section</code>{' '}
        выделяет секцию, <code className={styles.code}>h1</code> создает заголовок и т. д.), тег{' '}
        <code className={styles.code}>div</code> вообще не имеет никакого семантического значения.
      </p>
      <p className={styles.description}>
        Это может звучать странно, но они фактически используются для группировки тегов для последующей стилизации. До
        HTML5, <code className={styles.code}>div</code> использовался для группировки логически связанного контента,
        теперь же у нас есть семантические теги.
      </p>
      <p className={styles.description}>
        Тег <code className={styles.code}>div</code> относится к универсальным блочным контейнерам и применяется в тех
        случаях, где нужны блочные элементы без дополнительных свойств. В большинстве ситуаций используется как
        блок-обертка, с последующей стилизацией.
      </p>
      <p className={styles.description}>
        Как определить, перед нами <code className={styles.code}>article</code>,{' '}
        <code className={styles.code}>section</code> или <code className={styles.code}>div</code>?
      </p>

      <ul className={styles.list}>
        <li className={styles.list__item}>
          Если мы можем дать имя разделу и вынести этот раздел вне нашего сайта и он будет иметь смысл - это{' '}
          <code className={styles.code}>article</code>.
        </li>
        <li className={styles.list__item}>
          Если мы можем дать имя разделу и но вне нашего сайта этот раздел не будет иметь смысла - это{' '}
          <code className={styles.code}>section</code>.
        </li>
        <li className={styles.list__item}>
          Если же мы не можем дать вменяемого имени группе контента (не "правая колонка", а имеющее смысл) - это{' '}
          <code className={styles.code}>div</code> и скорее всего нам просто нужна группировка для стилизации.
        </li>
      </ul>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.7 - Фразовый контент</h3>

      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.7.1 - Ссылка</h4>
        <p className={styles.description}>
          <a
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.w3.org/TR/html53/textlevel-semantics.html%23the-a-element"
          >
            Якорь
          </a>{' '}
          — предназначен для создания ссылок. Внутрь тега a помещается текст, который будет отображаться на
          веб-странице. Текст ссылки отображается в браузере с подчёркиванием, цвет шрифта — синий, при наведении на
          ссылку курсор мыши меняет вид.
        </p>
        <p className={styles.description}>Атрибуты:</p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>href</code> — обязательным атрибут, задает адрес веб-страницы или некого
            ресурса (может содержать как абсолютный так и относительный путь).
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>download="путь к файлу"</code> — сообщает браузеру, что указанный в{' '}
            <code className={styles.code}>href</code> ресурс должен быть загружен в момент, когда пользователь щелкает
            по ссылке. Задавая имя для атрибута, мы таким образом задаем имя загружаемому файлу. Можно использовать
            атрибут без указания его значения.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>target</code> — указывает на то, в каком окне должен открываться документ, к
            которому ведет ссылка. Интересным значением можно выделить <code className={styles.code}>_blank</code>, при
            котором страница открывается в новом окне браузера.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>rel</code> — дополняет атрибут <code className={styles.code}>href</code>{' '}
            информацией об отношении между текущим и связанным документом.
          </li>
        </ul>
        <p className={styles.description}>
          У ссылок есть возможность не только переходить на другие страницы и скачивать файлы, но и совершать звонки на
          телефоны, отправлять сообщения или звонить по скайпу.
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="rotman_html_1_4_7_1"
          src="https://codepen.io/rotmanyan/embed/BawaamM?default-tab=html%2Cresult&editable=true&theme-id=dark"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>

      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.7.2 - Контентное изображение</h4>
        <p className={styles.description}>
          Использование графики делает веб-страницы визуально привлекательнее. Изображения помогают лучше передать суть
          и содержание документа.Тег <code className={styles.code}>img</code> предназначен для отображения на
          веб-странице изображений в различных графических форматах. Не все изображения на странице контентные,
          некоторые фоновые, об это в следующих модулях.
        </p>
        <p className={styles.description}>Атрибуты:</p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>src</code> — обязательный и указывает браузеру, где можно найти изображение.
            Как и тег <code className={styles.code}>a</code>, путь к картинке может быть абсолютным, или относительным.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>alt</code> — является альтернативным описанием. Это необходимо для
            предоставления значимой информации для пользователей, которые не могут видеть изображение (например, если
            они слабовидящие), или если картинка попросту не загрузилась.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>width</code> — задает ширину изображения.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>height</code> — задает высоту изображения.
          </li>
        </ul>
        <iframe
          height="300"
          scrolling="no"
          title="rotman_html_1_4_7_2"
          src="https://codepen.io/rotmanyan/embed/WNZNNzV?default-tab=html%2Cresult&editable=true&theme-id=dark"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
        <p className={styles.notificationWarn}>
          Без задания размеров изображения рисунок отображается на странице в реальном размере. Если будет задан только
          один из атрибутов, то второй будет вычисляться автоматически для сохранения пропорций рисунка.
        </p>
      </section>

      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.7.3 - Кнопка</h4>
        <p className={styles.description}>
          Часто тег <code className={styles.code}>button</code> это просто интерактивный элемент, который «оживляется» с
          помощью JavaScript. Например, кнопка закрытия всплывающего окна или прокрутки карусели.
        </p>
        <p className={styles.description}>
          Важно не путать ссылку и кнопку. Если при клике в элемент интерфейса происходит переход по какому-то адресу,
          то есть есть <code className={styles.code}>href</code>, то это ссылка. Если же при клике происходит что-то без
          перезагрузки или перенаправления страницы, то это кнопка.
        </p>
      </section>

      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.7.4 - span</h4>
        <p className={styles.description}>
          В отличии от абзаца, <code className={styles.code}>span</code> не начинается с новой строки. Он строчный,
          подчиняется правилу потока документа "слева направо" и занимает ширину по своему контенту. Предназначен для
          определения строчного элемента внутри документа, не заменяет тег <code className={styles.code}>p</code>. Может
          использоваться для группировки и выделения строчного контента внутри абзаца для дальнейшей стилизации.
        </p>
      </section>
    </section>

    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.8 - Пример использования тегов</h3>
      <p className={styles.description}>
        HTML-документ в примере ниже это не просто набор несвязного контента. Каждый тег несет смысл и описывает ту
        часть веб-страницы за которую отвечает.
      </p>
      <iframe
        height="300"
        scrolling="no"
        title="rotman_html_1_4_8"
        src="https://codepen.io/rotmanyan/embed/wvrvvjb?default-tab=html%2Cresult&editable=true&theme-id=dark"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>
  </section>
))
